import React, { useState } from "react";
import { Loader2, CheckCircle2, Mail, Send, Smartphone, ArrowLeft } from "lucide-react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const METHOD_META = {
  email: { icon: Mail, label: "Email code", hint: "We sent a 6-digit code to your email.", resend: true },
  telegram: { icon: Send, label: "Telegram code", hint: "Request a code to your connected Telegram.", resend: true },
  totp: { icon: Smartphone, label: "Authenticator app", hint: "Enter the 6-digit code from your authenticator app.", resend: false },
};

const inputCls = "w-full rounded-lg border border-white/[0.12] bg-[#0A0A0A] px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-white/30";

function MethodStep({ method, verified, sessionId, accent, onVerified, autoSent }) {
  const meta = METHOD_META[method] || METHOD_META.email;
  const Icon = meta.icon;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(!!autoSent);

  const sendCode = async () => {
    setBusy(true);
    try {
      await http.post("/auth/recovery/send-code", { session_id: sessionId, method });
      setSent(true);
      toast.success("Code sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the code"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    if (!code.trim()) return toast.error("Enter the code");
    setBusy(true);
    try {
      const { data } = await http.post("/auth/recovery/verify", { session_id: sessionId, method, code: code.trim() });
      onVerified(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid or expired code"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-xl border p-4 ${verified ? "border-emerald-500/30 bg-emerald-500/[0.05]" : "border-white/[0.1] bg-white/[0.02]"}`} data-testid={`recovery-step-${method}`}>
      <div className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 shrink-0" style={{ color: verified ? "#34d399" : accent }} />
        <span className="text-sm font-medium text-white">{meta.label}</span>
        {verified && <span className="ml-auto flex items-center gap-1 text-xs font-medium text-emerald-400" data-testid={`recovery-verified-${method}`}><CheckCircle2 className="h-3.5 w-3.5" /> Verified</span>}
      </div>
      {!verified && (
        <div className="mt-3 space-y-2.5">
          <p className="text-xs text-white/50">{meta.hint}</p>
          {meta.resend && method !== "email" && !sent ? (
            <Button size="sm" variant="outline" onClick={sendCode} disabled={busy} data-testid={`recovery-send-${method}`} className="border-white/15">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send code"}
            </Button>
          ) : (
            <div className="flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6}
                placeholder="123456" className={`${inputCls} tracking-[0.3em]`} data-testid={`recovery-code-${method}`} />
              <Button size="sm" onClick={verify} disabled={busy} data-testid={`recovery-verify-${method}`}
                className="shrink-0 self-stretch text-black hover:opacity-90" style={{ background: accent }}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Verify"}
              </Button>
            </div>
          )}
          {meta.resend && sent && (
            <button type="button" onClick={sendCode} disabled={busy} data-testid={`recovery-resend-${method}`}
              className="text-xs text-white/45 underline-offset-2 hover:text-white hover:underline">Resend code</button>
          )}
        </div>
      )}
    </div>
  );
}

export function RecoveryFlow({ accent = "#FFFFFF", onBack, backLabel = "Back to login" }) {
  const [stage, setStage] = useState("email"); // email | verify | password | done
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null); // {session_id, methods}
  const [verified, setVerified] = useState([]);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/auth/recovery/start", { email });
      setSession(data);
      setVerified([]);
      setStage("verify");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Too many attempts. Try again later."); }
    finally { setBusy(false); }
  };

  const onVerified = (data) => {
    setVerified(data.verified || []);
    if (data.all_verified) setStage("password");
  };

  const complete = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await http.post("/auth/recovery/complete", { session_id: session.session_id, password: pw });
      setStage("done");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Could not reset the password"); }
    finally { setBusy(false); }
  };

  if (stage === "done") {
    return (
      <div className="text-center" data-testid="recovery-done">
        <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: `${accent}22` }}>
          <CheckCircle2 className="h-6 w-6" style={{ color: accent }} />
        </span>
        <h2 className="text-lg font-semibold text-white">Password updated</h2>
        <p className="mt-2 text-sm text-white/60">All your sessions were signed out. Log in with your new password.</p>
        {onBack && <Button onClick={onBack} data-testid="recovery-back-login" className="mt-6 w-full text-black hover:opacity-90" style={{ background: accent }}>{backLabel}</Button>}
      </div>
    );
  }

  if (stage === "password") {
    return (
      <form onSubmit={complete} className="space-y-4" data-testid="recovery-password-form">
        <div>
          <h2 className="text-lg font-semibold text-white">Set a new password</h2>
          <p className="mt-1 text-xs text-white/50">Identity confirmed. Choose a new password (min. 8 characters).</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-white/70">New password</label>
          <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="recovery-new-password" autoComplete="new-password" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-white/70">Confirm password</label>
          <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputCls} data-testid="recovery-confirm-password" autoComplete="new-password" />
        </div>
        <Button type="submit" disabled={busy} data-testid="recovery-complete-btn" className="w-full text-black hover:opacity-90" style={{ background: accent }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
        </Button>
      </form>
    );
  }

  if (stage === "verify") {
    return (
      <div className="space-y-3" data-testid="recovery-verify">
        <div>
          <h2 className="text-lg font-semibold text-white">Confirm your identity</h2>
          <p className="mt-1 text-xs text-white/50" data-testid="recovery-generic-message">
            If an account with this email exists, recovery instructions have been sent.
            Complete {session.methods.length > 1 ? "ALL steps below" : "the step below"} to continue.
          </p>
        </div>
        {session.methods.map((m) => (
          <MethodStep key={m} method={m} verified={verified.includes(m)} sessionId={session.session_id}
            accent={accent} onVerified={onVerified} autoSent={m === "email"} />
        ))}
        {onBack && (
          <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-white/45 hover:text-white" data-testid="recovery-cancel">
            <ArrowLeft className="h-3 w-3" /> {backLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={start} className="space-y-4" data-testid="recovery-email-form">
      <div>
        <h2 className="text-lg font-semibold text-white">Forgot password</h2>
        <p className="mt-1 text-xs text-white/50">Enter your account email to start recovery.</p>
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-white/70">Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls}
          placeholder="you@example.com" data-testid="recovery-email-input" />
      </div>
      <Button type="submit" disabled={busy} data-testid="recovery-start-btn" className="w-full text-black hover:opacity-90" style={{ background: accent }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
      </Button>
      {onBack && (
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-white/45 hover:text-white" data-testid="recovery-back">
          <ArrowLeft className="h-3 w-3" /> {backLabel}
        </button>
      )}
    </form>
  );
}
